import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getEnv } from '@/env';

export interface Plan {
    id: number;
    uuid: string;
    name: string;
    slug: string;
    type: string;
    user_type: string;
    term: string | null;
    price: number;
    stripe_price_id: string;
    stripe_product_id: string;
    grantable_type: string;
    grantable_id: number;
    allow_multiple: boolean;
    is_legacy: number;
    plan: string;
    frequency: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export const usePlanStore = defineStore('plan', () => {
    // State
    const planData = ref<Plan | null>(null);
    const planFrequencies = ref<any>(null);
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const currentPlan = ref<string>('');
    const currentFrequency = ref<string>('');

    // Getters (computed)
    const isValidPlanParams = computed(() =>
        currentPlan.value && currentFrequency.value
    );

    const planFrequencyString = computed(() => {
        if (isValidPlanParams.value) {
            return `${currentPlan.value}-${currentFrequency.value}`;
        }
        return '';
    });

    const hasData = computed(() => planData.value !== null);

    const hasFrequencyData = computed(() => planFrequencies.value !== null);

    // Actions
    const extractPlanParamsFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const plan = urlParams.get('plan') || '';
        const frequency = urlParams.get('frequency') || '';

        currentPlan.value = plan;
        currentFrequency.value = frequency;

        return { plan, frequency };
    };

    const updateUrlParams = (plan: string, frequency: string) => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('plan', plan);
        urlParams.set('frequency', frequency);

        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState(null, '', newUrl);

        currentPlan.value = plan;
        currentFrequency.value = frequency;
    };

    const updateFrequency = (newFrequency: string) => {
        updateUrlParams(currentPlan.value, newFrequency);
        return fetchPlanData();
    };

    const fetchPlanData = async () => {
        if (!isValidPlanParams.value) {
            error.value = 'Missing plan or frequency parameters';
            isLoading.value = false;
            return null;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const response = await fetch(
                `${getEnv('VITE_APP_URL')}/api/public/products/${planFrequencyString.value}`
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch plan data: ${response.statusText}`);
            }

            const data = await response.json();
            planData.value = data;
            return data;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            error.value = errorMessage;
            console.error('Error fetching plan data:', err);
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const fetchPlanFrequencies = async (planName?: string) => {
        const planToUse = planName || planFrequencyString.value;

        if (!planToUse) {
            error.value = 'Missing plan parameters for frequency fetch';
            return null;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const response = await fetch(
                `${getEnv('VITE_APP_URL')}/api/public/products/${planToUse}/pricing`
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch plan frequencies: ${response.statusText}`);
            }

            const data = await response.json();
            planFrequencies.value = data;
            return data;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            error.value = errorMessage;
            console.error('Error fetching plan frequencies:', err);
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const initializePlanData = async () => {
        extractPlanParamsFromUrl();
        if (isValidPlanParams.value) {
            // Fetch both plan data and available frequencies
            const [planResult, frequenciesResult] = await Promise.allSettled([
                fetchPlanData(),
                fetchPlanFrequencies()
            ]);

            if (planResult.status === 'fulfilled') {
                return planResult.value;
            } else {
                console.error('Failed to fetch plan data:', planResult.reason);
                return null;
            }
        }
        return null;
    };

    const clearPlanData = () => {
        planData.value = null;
        planFrequencies.value = null;
        error.value = null;
        currentPlan.value = '';
        currentFrequency.value = '';
    };

    const setPlanParams = (plan: string, frequency: string) => {
        currentPlan.value = plan;
        currentFrequency.value = frequency;
        updateUrlParams(plan, frequency);
    };

    // Reset function for cleanup
    const $reset = () => {
        planData.value = null;
        planFrequencies.value = null;
        isLoading.value = false;
        error.value = null;
        currentPlan.value = '';
        currentFrequency.value = '';
    };

    return {
        // State
        planData,
        planFrequencies,
        isLoading,
        error,
        currentPlan,
        currentFrequency,

        // Getters
        isValidPlanParams,
        planFrequencyString,
        hasData,
        hasFrequencyData,

        // Actions
        extractPlanParamsFromUrl,
        updateUrlParams,
        updateFrequency,
        fetchPlanData,
        fetchPlanFrequencies,
        initializePlanData,
        clearPlanData,
        setPlanParams,
        $reset
    };
});
